import { useState } from 'react';
import { useLocale } from '../hooks/useLocale.jsx';
import { usePaintMask, BRUSH_MIN, BRUSH_MAX } from '../hooks/usePaintMask.js';

// 평면도(또는 3D 평면도 결과) 위에 방 하나를 칠해 고르는 창 (2.4.0, ROADMAP 4번).
// 칠한 사본은 반투명 빨강 — 방 안의 창·문이 비쳐야 모델이 그 방 모양을 읽는다.
// 만들기 화면은 고른 사본만 받아 두고(onSubmit), 3D 평면도 상세 화면은 바로 작업을 건다.
export function PlanRoomPicker({ src, remote = false, submitLabel, meta = null, onSubmit, onClose }) {
  const { t } = useLocale();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // 원격 이미지는 SW 의 opaque 캐시 때문에 CORS 로드가 실패할 수 있다 — EditRegionModal 과 같은 재시도.
  const [retry, setRetry] = useState(0);
  const imageSrc = remote && retry > 0 ? `${src}${src.includes('?') ? '&' : '?'}cb=${retry}` : src;
  const { imgRef, canvasRef, hasMask, brushSize, setBrushSize, undoStroke, clearMask, composeOverlayBlob, handlers } = usePaintMask({ src: imageSrc, defaultBrush: 90 });

  const submit = async () => {
    setSubmitting(true);
    try {
      const blob = await composeOverlayBlob({ tint: 0.45 });
      await onSubmit(blob);
    } catch (err) {
      console.error('Room pick failed:', err);
      setError(t('errEditGeneric'));
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !submitting && onClose()}>
      <div className="modal-card edit-region-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} disabled={submitting} aria-label="Close">
          <i className="material-symbols-rounded">close</i>
        </button>
        <h3>{t('planRoomPickTitle')}</h3>
        <p className="modal-desc">{t('planRoomPickHint')}</p>

        <div className="edit-region-canvas-wrap">
          <img
            ref={imgRef}
            src={imageSrc}
            alt=""
            className="edit-region-image"
            {...(remote && { crossOrigin: 'anonymous' })}
            onError={() => {
              if (remote && retry < 2) setRetry(r => r + 1);
              else setError(t('errEditGeneric'));
            }}
          />
          <canvas ref={canvasRef} className="edit-region-canvas" {...handlers} />
        </div>

        <div className="edit-region-tools">
          <label className="edit-tool-size">
            <span className="edit-tool-size-label">{t('editRegionSize')}</span>
            <input
              type="range"
              min={BRUSH_MIN}
              max={BRUSH_MAX}
              step="2"
              value={brushSize}
              style={{ '--range-pct': `${((brushSize - BRUSH_MIN) / (BRUSH_MAX - BRUSH_MIN)) * 100}%` }}
              onChange={e => setBrushSize(Number(e.target.value))}
              disabled={submitting}
            />
          </label>
          <button type="button" className="edit-tool-btn" onClick={undoStroke} disabled={submitting || !hasMask}>
            <i className="material-symbols-rounded">undo</i>
            <span>{t('editRegionUndo')}</span>
          </button>
          <button type="button" className="edit-tool-btn edit-tool-clear" onClick={clearMask} disabled={submitting || !hasMask}>
            <i className="material-symbols-rounded">restart_alt</i>
            <span>{t('editRegionClear')}</span>
          </button>
        </div>

        {meta && <p className="modal-meta">{meta}</p>}
        {error && <p className="edit-region-error">{error}</p>}

        <div className="controls" style={{ marginTop: '1rem' }}>
          <button className="btn btn-primary" onClick={submit} disabled={submitting || !hasMask}>
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
